import React, { useEffect, useMemo, useRef, useState } from "react";
import { Helmet } from "react-helmet";
import Hls from "hls.js";
import {
    ArrowBack,
    Close,
    Favorite,
    FavoriteBorder,
    FilterAlt,
    Fullscreen,
    GridView,
    LiveTv,
    Logout,
    Pause,
    PlayArrow,
    Search,
    Tune,
    ViewList,
    VolumeOff,
    VolumeUp,
} from "@mui/icons-material";
import { bootstrapTv, createTvPassword, fetchTvChannels, loginToTv, logoutFromTv, requestTvAccess } from "./tvApi";
import styles from "./TV.module.css";

const FAVORITES_KEY = "tv-favorites-v1";
const RECENTS_KEY = "tv-recents-v1";
const PAGE_SIZE = 72;

const readStoredList = (key) => {
    try {
        const value = JSON.parse(window.localStorage.getItem(key) || "[]");
        return Array.isArray(value) ? value : [];
    } catch (error) {
        return [];
    }
};

const initials = (name) =>
    String(name || "TV")
        .split(/\s+/)
        .slice(0, 2)
        .map((word) => word.charAt(0))
        .join("")
        .toUpperCase();

const titleCase = (value) =>
    String(value || "Other")
        .replace(/[-_]/g, " ")
        .replace(/\b\w/g, (letter) => letter.toUpperCase());

const getInitialism = (value) =>
    String(value || "")
        .split(/[^a-zA-Z0-9]+/)
        .filter(Boolean)
        .map((word) => word.charAt(0))
        .join("")
        .toLowerCase();

const ChannelLogo = ({ channel }) => {
    const [failed, setFailed] = useState(false);
    return channel.logo && !failed ? (
        <img src={channel.logo} alt="" loading="lazy" onError={() => setFailed(true)} />
    ) : (
        <span>{initials(channel.name)}</span>
    );
};

const Player = ({ channel, mode, onClose, onRestore }) => {
    const videoRef = useRef(null);
    const hlsRef = useRef(null);
    const [status, setStatus] = useState("loading");
    const [isPlaying, setIsPlaying] = useState(false);
    const [muted, setMuted] = useState(false);

    useEffect(() => {
        const video = videoRef.current;
        if (!video || !channel) {
            return undefined;
        }

        setStatus("loading");
        setIsPlaying(false);

        const handleReady = () => {
            setStatus("ready");
            video.play().catch(() => {});
        };
        const handleError = () => setStatus("error");
        video.addEventListener("loadedmetadata", handleReady);
        video.addEventListener("canplay", handleReady);
        video.addEventListener("error", handleError);

        if (Hls.isSupported() && /\.m3u8(?:$|\?)/i.test(channel.url)) {
            const hls = new Hls({
                enableWorker: true,
                lowLatencyMode: true,
                backBufferLength: 30,
            });
            hlsRef.current = hls;
            hls.loadSource(channel.url);
            hls.attachMedia(video);
            hls.on(Hls.Events.MANIFEST_PARSED, handleReady);
            hls.on(Hls.Events.ERROR, (event, data) => {
                if (data.fatal) {
                    setStatus("error");
                }
            });
        } else {
            video.src = channel.url;
            video.load();
        }

        return () => {
            video.removeEventListener("loadedmetadata", handleReady);
            video.removeEventListener("canplay", handleReady);
            video.removeEventListener("error", handleError);
            if (hlsRef.current) {
                hlsRef.current.destroy();
                hlsRef.current = null;
            }
            video.removeAttribute("src");
            video.load();
        };
    }, [channel]);

    const togglePlayback = () => {
        const video = videoRef.current;
        if (!video) return;
        if (video.paused) {
            video.play().catch(() => setStatus("error"));
        } else {
            video.pause();
        }
    };

    const toggleFullscreen = () => {
        const target = videoRef.current;
        if (target && target.requestFullscreen) {
            target.requestFullscreen();
        }
    };

    return (
        <section className={`${styles.player} ${mode === "full" ? styles.playerFull : styles.playerDocked}`}>
            <video
                ref={videoRef}
                autoPlay
                playsInline
                muted={muted}
                onPlay={() => setIsPlaying(true)}
                onPause={() => setIsPlaying(false)}
            />
            <div className={styles.playerShade} />
            {status === "loading" && <div className={styles.playerStatus}><span className={styles.spinner} />Tuning channel…</div>}
            {status === "error" && (
                <div className={styles.playerError}>
                    <strong>Channel unavailable</strong>
                    <span>The stream may be offline, geo-blocked, or blocked by your browser.</span>
                </div>
            )}
            <div className={styles.playerTop}>
                {mode === "full" && (
                    <button type="button" onClick={onRestore} aria-label="Restore guide"><ArrowBack /> Guide</button>
                )}
                <div className={styles.playerIdentity}>
                    <div className={styles.miniLogo}><ChannelLogo channel={channel} /></div>
                    <div><strong>{channel.name}</strong><span>{channel.countryName || channel.country || "Worldwide"} · {titleCase(channel.category)}</span></div>
                </div>
                <button type="button" className={styles.iconButton} onClick={onClose} aria-label="Close player"><Close /></button>
            </div>
            <div className={styles.playerControls}>
                <button type="button" className={styles.roundControl} onClick={togglePlayback} aria-label={isPlaying ? "Pause" : "Play"}>
                    {isPlaying ? <Pause /> : <PlayArrow />}
                </button>
                <button type="button" className={styles.iconButton} onClick={() => setMuted((value) => !value)} aria-label={muted ? "Unmute" : "Mute"}>
                    {muted ? <VolumeOff /> : <VolumeUp />}
                </button>
                <span className={styles.liveBadge}><i /> LIVE</span>
                <span className={styles.playerChannelName}>{channel.name}</span>
                {mode === "docked" && <button type="button" className={styles.textControl} onClick={onRestore}><Fullscreen /> Expand</button>}
                <button type="button" className={styles.iconButton} onClick={toggleFullscreen} aria-label="Browser fullscreen"><Fullscreen /></button>
            </div>
        </section>
    );
};

const TVApp = () => {
    const setupToken = new URLSearchParams(window.location.search).get("token") || "";
    const [currentUser, setCurrentUser] = useState(null);
    const [authReady, setAuthReady] = useState(false);
    const [loginForm, setLoginForm] = useState({ identifier: "", password: "" });
    const [signupForm, setSignupForm] = useState({ username: "", name: "", code: "" });
    const [passwordForm, setPasswordForm] = useState({ password: "", confirmPassword: "" });
    const [setupUrl, setSetupUrl] = useState("");
    const [authMode, setAuthMode] = useState(setupToken ? "create-password" : "login");
    const [channels, setChannels] = useState([]);
    const [isLoadingChannels, setIsLoadingChannels] = useState(false);
    const [loadError, setLoadError] = useState("");
    const [authError, setAuthError] = useState("");
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [search, setSearch] = useState("");
    const [country, setCountry] = useState("all");
    const [category, setCategory] = useState("all");
    const [language, setLanguage] = useState("all");
    const [favoritesOnly, setFavoritesOnly] = useState(false);
    const [sort, setSort] = useState("popular");
    const [view, setView] = useState("grid");
    const [visibleCount, setVisibleCount] = useState(PAGE_SIZE);
    const [filtersOpen, setFiltersOpen] = useState(false);
    const [selectedChannel, setSelectedChannel] = useState(null);
    const [playerMode, setPlayerMode] = useState("full");
    const [favorites, setFavorites] = useState(() => readStoredList(FAVORITES_KEY));
    const [recents, setRecents] = useState(() => readStoredList(RECENTS_KEY));
    const searchRef = useRef(null);

    useEffect(() => {
        bootstrapTv()
            .then((payload) => setCurrentUser(payload.currentUser || null))
            .catch(() => setCurrentUser(null))
            .finally(() => setAuthReady(true));
    }, []);

    useEffect(() => {
        if (!currentUser) return;
        setIsLoadingChannels(true);
        setLoadError("");
        fetchTvChannels()
            .then((payload) => setChannels(payload.channels || []))
            .catch((error) => setLoadError(error.message || "Unable to load the channel guide."))
            .finally(() => setIsLoadingChannels(false));
    }, [currentUser]);

    useEffect(() => {
        const onKeyDown = (event) => {
            if ((event.ctrlKey || event.metaKey) && event.key.toLowerCase() === "k") {
                event.preventDefault();
                searchRef.current && searchRef.current.focus();
            }
            if (event.key === "Escape") {
                if (filtersOpen) setFiltersOpen(false);
                else if (playerMode === "full" && selectedChannel) setPlayerMode("docked");
            }
        };
        window.addEventListener("keydown", onKeyDown);
        return () => window.removeEventListener("keydown", onKeyDown);
    }, [filtersOpen, playerMode, selectedChannel]);

    const facets = useMemo(() => {
        const collect = (key, fallback) => {
            const counts = new Map();
            channels.forEach((channel) => {
                const value = channel[key] || fallback;
                counts.set(value, (counts.get(value) || 0) + 1);
            });
            return Array.from(counts.entries()).sort((a, b) =>
                titleCase(a[0]).localeCompare(titleCase(b[0]), undefined, { sensitivity: "base" })
            );
        };
        return {
            countries: collect("countryName", "Worldwide"),
            categories: collect("category", "other"),
            languages: collect("language", "Unknown"),
        };
    }, [channels]);

    const filteredChannels = useMemo(() => {
        const query = search.trim().toLowerCase();
        const favoriteSet = new Set(favorites);
        const recentRanks = new Map(recents.map((id, index) => [id, index]));

        return channels
            .filter((channel) => {
                const searchableValues = [
                    channel.name,
                    channel.channelId,
                    channel.network,
                    channel.countryName,
                    channel.country,
                    ...(channel.altNames || []),
                    ...(channel.owners || []),
                    ...(channel.categories || [channel.category]),
                    ...(channel.languages || [channel.language]),
                ].filter(Boolean);
                const queryTerms = query.split(/\s+/).filter(Boolean);
                const matchesQuery = !queryTerms.length || queryTerms.every((term) =>
                    searchableValues.some((value) => {
                        const normalizedValue = String(value).toLowerCase();
                        return normalizedValue.includes(term) || getInitialism(value).includes(term);
                    })
                );
                return matchesQuery
                    && (country === "all" || (channel.countryName || "Worldwide") === country)
                    && (category === "all" || (channel.category || "other") === category)
                    && (language === "all" || (channel.language || "Unknown") === language)
                    && (!favoritesOnly || favoriteSet.has(channel.id));
            })
            .sort((a, b) => {
                if (sort === "az") return a.name.localeCompare(b.name);
                if (sort === "country") return (a.countryName || "").localeCompare(b.countryName || "") || a.name.localeCompare(b.name);
                if (sort === "recent") {
                    const aRank = recentRanks.has(a.id) ? recentRanks.get(a.id) : 99999;
                    const bRank = recentRanks.has(b.id) ? recentRanks.get(b.id) : 99999;
                    return aRank - bRank;
                }
                return Number(favoriteSet.has(b.id)) - Number(favoriteSet.has(a.id)) || a.name.localeCompare(b.name);
            });
    }, [channels, search, country, category, language, favoritesOnly, favorites, recents, sort]);

    useEffect(() => setVisibleCount(PAGE_SIZE), [search, country, category, language, favoritesOnly, sort]);

    const submitLogin = async (event) => {
        event.preventDefault();
        setIsSubmitting(true);
        setAuthError("");
        try {
            const payload = await loginToTv(loginForm);
            setCurrentUser(payload.currentUser);
            setLoginForm({ identifier: "", password: "" });
        } catch (error) {
            setAuthError(error.message || "Unable to sign in.");
        } finally {
            setIsSubmitting(false);
        }
    };

    const submitSignup = async (event) => {
        event.preventDefault();
        setAuthError("");
        setIsSubmitting(true);
        try {
            const payload = await requestTvAccess(signupForm);
            setSetupUrl(payload.setupUrl || "");
            setSignupForm({ username: "", name: "", code: "" });
        } catch (error) {
            setAuthError(error.message || "Unable to request access.");
        } finally {
            setIsSubmitting(false);
        }
    };

    const submitPassword = async (event) => {
        event.preventDefault();
        setAuthError("");
        if (passwordForm.password !== passwordForm.confirmPassword) {
            setAuthError("Passwords do not match.");
            return;
        }

        setIsSubmitting(true);
        try {
            await createTvPassword({ token: setupToken, password: passwordForm.password });
            window.history.replaceState({}, "", "/tv");
            setPasswordForm({ password: "", confirmPassword: "" });
            setAuthMode("login");
        } catch (error) {
            setAuthError(error.message || "Unable to create password.");
        } finally {
            setIsSubmitting(false);
        }
    };
    const logout = async () => {
        await logoutFromTv().catch(() => {});
        setCurrentUser(null);
        setChannels([]);
        setSelectedChannel(null);
    };

    const playChannel = (channel) => {
        if (!channel.isAvailable || !channel.url) return;
        setSelectedChannel(channel);
        setPlayerMode("full");
        setRecents((previous) => {
            const next = [channel.id, ...previous.filter((id) => id !== channel.id)].slice(0, 30);
            window.localStorage.setItem(RECENTS_KEY, JSON.stringify(next));
            return next;
        });
    };

    const toggleFavorite = (channelId) => {
        setFavorites((previous) => {
            const next = previous.includes(channelId)
                ? previous.filter((id) => id !== channelId)
                : [channelId, ...previous];
            window.localStorage.setItem(FAVORITES_KEY, JSON.stringify(next));
            return next;
        });
    };

    const clearFilters = () => {
        setSearch("");
        setCountry("all");
        setCategory("all");
        setLanguage("all");
        setFavoritesOnly(false);
    };

    if (!authReady) {
        return <main className={styles.splash}><span className={styles.spinner} /><span>Starting television…</span></main>;
    }

    if (!currentUser) {
        return (
            <main className={styles.loginPage}>
                <Helmet><title>Live TV · rjbogz</title></Helmet>
                <div className={styles.loginGlowOne} />
                <div className={styles.loginGlowTwo} />
                <section className={styles.loginCard}>
                    <div className={styles.brandMark}><LiveTv /></div>
                    {authMode === "login" ? (
                        <form onSubmit={submitLogin}>
                            <label className={styles.srOnly} htmlFor="tv-username">Username</label>
                            <input
                                id="tv-username"
                                aria-label="Username"
                                type="text"
                                autoComplete="username"
                                value={loginForm.identifier}
                                onChange={(event) => setLoginForm((previous) => ({ ...previous, identifier: event.target.value }))}
                                placeholder="Username"
                                required
                            />
                            <label className={styles.srOnly} htmlFor="tv-password">Password</label>
                            <input
                                id="tv-password"
                                aria-label="Password"
                                type="password"
                                autoComplete="current-password"
                                value={loginForm.password}
                                onChange={(event) => setLoginForm((previous) => ({ ...previous, password: event.target.value }))}
                                placeholder="Password"
                                required
                            />
                            {authError && <p className={styles.formError}>{authError}</p>}
                            <button className={styles.loginButton} type="submit" disabled={isSubmitting}>
                                {isSubmitting ? "Submitting…" : "Submit"}
                            </button>
                            <button className={styles.authModeButton} type="button" onClick={() => { setAuthError(""); setAuthMode("signup"); }}>
                                Request access
                            </button>
                        </form>
                    ) : authMode === "create-password" ? (
                        <form onSubmit={submitPassword}>
                            <label className={styles.srOnly} htmlFor="tv-new-password">Password</label>
                            <input
                                id="tv-new-password"
                                aria-label="Password"
                                type="password"
                                autoComplete="new-password"
                                value={passwordForm.password}
                                onChange={(event) => setPasswordForm((previous) => ({ ...previous, password: event.target.value }))}
                                placeholder="Password"
                                required
                            />
                            <label className={styles.srOnly} htmlFor="tv-new-password-confirm">Confirm password</label>
                            <input
                                id="tv-new-password-confirm"
                                aria-label="Confirm password"
                                type="password"
                                autoComplete="new-password"
                                value={passwordForm.confirmPassword}
                                onChange={(event) => setPasswordForm((previous) => ({ ...previous, confirmPassword: event.target.value }))}
                                placeholder="Confirm password"
                                required
                            />
                            {authError && <p className={styles.formError}>{authError}</p>}
                            <button className={styles.loginButton} type="submit" disabled={isSubmitting}>
                                {isSubmitting ? "Submitting…" : "Submit"}
                            </button>
                        </form>
                    ) : (
                        <form onSubmit={submitSignup}>
                            <label className={styles.srOnly} htmlFor="tv-signup-username">Username</label>
                            <input
                                id="tv-signup-username"
                                aria-label="Username"
                                type="text"
                                autoComplete="username"
                                value={signupForm.username}
                                onChange={(event) => setSignupForm((previous) => ({ ...previous, username: event.target.value }))}
                                placeholder="Username"
                                required
                            />
                            <label className={styles.srOnly} htmlFor="tv-signup-name">Name</label>
                            <input
                                id="tv-signup-name"
                                aria-label="Name"
                                type="text"
                                autoComplete="name"
                                value={signupForm.name}
                                onChange={(event) => setSignupForm((previous) => ({ ...previous, name: event.target.value }))}
                                placeholder="Name"
                                required
                            />
                            <label className={styles.srOnly} htmlFor="tv-signup-code">Signup code</label>
                            <input
                                id="tv-signup-code"
                                aria-label="Signup code"
                                type="text"
                                autoComplete="one-time-code"
                                value={signupForm.code}
                                onChange={(event) => setSignupForm((previous) => ({ ...previous, code: event.target.value }))}
                                placeholder="Signup code"
                                required
                            />
                            {authError && <p className={styles.formError}>{authError}</p>}
                            {setupUrl ? (
                                <a className={styles.setupLink} href={setupUrl}>Create password</a>
                            ) : (
                                <button className={styles.loginButton} type="submit" disabled={isSubmitting}>
                                    {isSubmitting ? "Submitting…" : "Submit"}
                                </button>
                            )}
                            <button className={styles.authModeButton} type="button" onClick={() => { setAuthError(""); setAuthMode("login"); }}>
                                Back to login
                            </button>
                        </form>
                    )}
                </section>
            </main>
        );
    }

    const activeFilterCount = [country !== "all", category !== "all", language !== "all", favoritesOnly].filter(Boolean).length;

    return (
        <main className={`${styles.app} ${selectedChannel && playerMode === "docked" ? styles.withDockedPlayer : ""}`}>
            <Helmet><title>Live TV Guide · rjbogz</title></Helmet>
            <header className={styles.header}>
                <div className={styles.brand}><span><LiveTv /></span><div><strong>LIVE TV</strong><small>rjbogz guide</small></div></div>
                <div className={styles.searchBox}>
                    <Search />
                    <input ref={searchRef} value={search} onChange={(event) => setSearch(event.target.value)} placeholder="Search channels, countries, categories…" />
                    {search && <button type="button" onClick={() => setSearch("")} aria-label="Clear search"><Close /></button>}
                    <kbd>Ctrl K</kbd>
                </div>
                <button type="button" className={styles.mobileFilterButton} onClick={() => setFiltersOpen(true)}><FilterAlt />{activeFilterCount > 0 && <b>{activeFilterCount}</b>}</button>
                <div className={styles.userMenu}><span>{initials(currentUser.username)}</span><div><strong>{currentUser.username}</strong></div><button type="button" onClick={logout} aria-label="Sign out"><Logout /></button></div>
            </header>

            <div className={styles.shell}>
                <aside className={`${styles.sidebar} ${filtersOpen ? styles.sidebarOpen : ""}`}>
                    <div className={styles.sidebarMobileHead}><strong>Filters</strong><button onClick={() => setFiltersOpen(false)}><Close /></button></div>
                    <button type="button" className={`${styles.quickFilter} ${!favoritesOnly ? styles.quickFilterActive : ""}`} onClick={() => setFavoritesOnly(false)}><LiveTv /> All channels <span>{channels.length}</span></button>
                    <button type="button" className={`${styles.quickFilter} ${favoritesOnly ? styles.quickFilterActive : ""}`} onClick={() => setFavoritesOnly(true)}><Favorite /> Favorites <span>{favorites.length}</span></button>
                    <div className={styles.filterTitle}><span><Tune /> Refine guide</span>{activeFilterCount > 0 && <button onClick={clearFilters}>Clear</button>}</div>
                    <label className={styles.selectLabel}>Country<select value={country} onChange={(event) => setCountry(event.target.value)}><option value="all">All countries</option>{facets.countries.map(([value, count]) => <option key={value} value={value}>{value} ({count})</option>)}</select></label>
                    <label className={styles.selectLabel}>Category<select value={category} onChange={(event) => setCategory(event.target.value)}><option value="all">All categories</option>{facets.categories.map(([value, count]) => <option key={value} value={value}>{titleCase(value)} ({count})</option>)}</select></label>
                    <label className={styles.selectLabel}>Language<select value={language} onChange={(event) => setLanguage(event.target.value)}><option value="all">All languages</option>{facets.languages.map(([value, count]) => <option key={value} value={value}>{value} ({count})</option>)}</select></label>
                    <div className={styles.sidebarFoot}><i /><span>Playlist refreshed by the server</span></div>
                </aside>
                {filtersOpen && <button className={styles.scrim} onClick={() => setFiltersOpen(false)} aria-label="Close filters" />}

                <section className={styles.guide}>
                    <div className={styles.guideTop}>
                        <div><p className={styles.eyebrow}>LIVE CHANNEL GUIDE</p><h1>{favoritesOnly ? "Your favorites" : "What’s on now"}</h1><p>{isLoadingChannels ? "Tuning the guide…" : `${filteredChannels.length.toLocaleString()} channels found`}</p></div>
                        <div className={styles.viewControls}>
                            <label>Sort<select value={sort} onChange={(event) => setSort(event.target.value)}><option value="popular">Recommended</option><option value="az">A–Z</option><option value="country">Country</option><option value="recent">Recently watched</option></select></label>
                            <div><button className={view === "grid" ? styles.selected : ""} onClick={() => setView("grid")} aria-label="Grid view"><GridView /></button><button className={view === "list" ? styles.selected : ""} onClick={() => setView("list")} aria-label="List view"><ViewList /></button></div>
                        </div>
                    </div>
                    {loadError && <div className={styles.notice}><strong>Guide unavailable</strong><span>{loadError}</span><button onClick={() => window.location.reload()}>Try again</button></div>}
                    {isLoadingChannels && <div className={styles.loadingGrid}>{Array.from({ length: 12 }).map((_, index) => <span key={index} />)}</div>}
                    {!isLoadingChannels && !loadError && filteredChannels.length === 0 && <div className={styles.empty}><Search /><h2>No channels found</h2><p>Try a broader search or clear your filters.</p><button onClick={clearFilters}>Clear filters</button></div>}
                    <div className={view === "grid" ? styles.channelGrid : styles.channelList}>
                        {filteredChannels.slice(0, visibleCount).map((channel) => (
                            <article className={`${styles.channelCard} ${!channel.isAvailable ? styles.channelUnavailable : ""}`} key={channel.id}>
                                <button
                                    type="button"
                                    className={styles.channelVisual}
                                    onClick={() => playChannel(channel)}
                                    aria-label={channel.isAvailable ? `Watch ${channel.name}` : `${channel.name} has no public stream`}
                                    disabled={!channel.isAvailable}
                                >
                                    <div className={styles.channelLogo}><ChannelLogo channel={channel} /></div>
                                    {channel.isAvailable ? <span className={styles.playButton}><PlayArrow /></span> : <span className={styles.noStream}>No stream</span>}
                                    <span className={`${styles.onAir} ${!channel.isAvailable ? styles.offAir : ""}`}><i /> {channel.isAvailable ? "LIVE" : "DATABASE"}</span>
                                </button>
                                <div className={styles.channelDetails}>
                                    <div className={styles.channelHeading}><div><h2>{channel.name}</h2><p>{channel.countryName || "Worldwide"} · {channel.language || "Unknown language"}</p></div><button type="button" onClick={() => toggleFavorite(channel.id)} aria-label={favorites.includes(channel.id) ? "Remove favorite" : "Add favorite"}>{favorites.includes(channel.id) ? <Favorite /> : <FavoriteBorder />}</button></div>
                                    <div className={styles.program}><span>{titleCase(channel.category)}</span><strong>{channel.isAvailable ? (channel.streamQuality || "Live programming") : "No public stream"}</strong><small>{channel.isAvailable ? `${channel.streamCount || 1} stream${channel.streamCount === 1 ? "" : "s"} available` : "IPTV-org database entry"}</small></div>
                                </div>
                            </article>
                        ))}
                    </div>
                    {visibleCount < filteredChannels.length && <button className={styles.loadMore} onClick={() => setVisibleCount((count) => count + PAGE_SIZE)}>Show more channels</button>}
                </section>
            </div>

            {selectedChannel && <Player channel={selectedChannel} mode={playerMode} onRestore={() => setPlayerMode(playerMode === "full" ? "docked" : "full")} onClose={() => setSelectedChannel(null)} />}
        </main>
    );
};

export default TVApp;
